import React, { useRef, useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiChevronRight, FiChevronLeft } from 'react-icons/fi';

export default function CategoryNav({ categories = [], activeCategory, setActiveCategory }) {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 20);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 20);
    }
  };

  useEffect(() => {
    // Initial check
    const timeoutId = setTimeout(checkScroll, 100);
    window.addEventListener('resize', checkScroll);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScroll);
    };
  }, [categories]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -250 : 250;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      // Re-check after animation
      setTimeout(checkScroll, 350);
    }
  };

  return (
    <div className="relative flex items-center bg-retro-cream border-b-4 border-retro-brown w-full group">
      {/* Left Arrow & Gradient */}
      <div 
        className={`absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-retro-cream via-retro-cream/80 to-transparent pointer-events-none z-10 transition-opacity duration-300 ${showLeftArrow ? 'opacity-100' : 'opacity-0'}`} 
      />
      
      {showLeftArrow && (
        <button 
          onClick={() => handleScroll('left')}
          className="absolute left-4 z-20 w-10 h-10 flex items-center justify-center bg-retro-yellow border-2 border-retro-brown rounded-full shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-retro-active text-retro-brown font-bold transition-all animate-in fade-in slide-in-from-left-2"
        >
          <SafeIcon icon={FiChevronLeft} className="text-xl" />
        </button>
      )}

      {/* Categories List */}
      <div 
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className="flex overflow-x-auto hide-scrollbar gap-3 py-5 px-6 w-full items-center"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((category) => (
          <button 
            key={category} 
            onClick={() => setActiveCategory(category)}
            className={`whitespace-nowrap px-5 py-2 font-bold text-[16px] transition-all border-2 border-retro-brown rounded-full shadow-retro-sm ${
              activeCategory === category 
                ? 'bg-retro-teal text-white shadow-retro -translate-y-0.5' 
                : 'bg-white text-retro-brown hover:bg-retro-yellow hover:-translate-y-0.5 hover:shadow-retro'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Right Arrow & Gradient */}
      <div 
        className={`absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-retro-cream via-retro-cream/80 to-transparent pointer-events-none z-10 transition-opacity duration-300 ${showRightArrow ? 'opacity-100' : 'opacity-0'}`} 
      />
      
      {showRightArrow && (
        <button 
          onClick={() => handleScroll('right')}
          className="absolute right-4 z-20 w-10 h-10 flex items-center justify-center bg-retro-yellow border-2 border-retro-brown rounded-full shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-retro-active text-retro-brown font-bold transition-all animate-in fade-in slide-in-from-right-2"
        >
          <SafeIcon icon={FiChevronRight} className="text-xl" />
        </button>
      )}

      <style jsx="true">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}